// src/pages/markets/board/card/EventOutcomesPanel.tsx
//
// Every market of the event, not just the one the collapsed row picked. Ordered
// the way the mode reads: dependent outcomes compete, so probability ranks them;
// independent ones do not compare, so depth does. The card is the short form;
// this panel is everything the board knows about the event.

import { Box, Button, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import type {
    BoardScore,
    EventConvictionIndex,
    ScreenerEvent,
    ScreenerMarket,
} from '../api/boardTypes';
import { leadScore, netCapital, netSide } from '../api/netCapital';
import { useEventOutcomes } from '../api/useEventOutcomes';
import { useEventOutcomeImages } from '../api/useEventOutcomeImages';
import type { CardVariant } from '../board/columnConfig';
import { orderTags } from '../board/columnConfig';
import { eventHref, eventMarketHref } from '../board/routes';
import { needsTrueOutcomes, sideLabels } from '../utils/boardOutcomeLabels';
import { sideColor } from '../utils/sideColor';
import { OUTCOME_MAXLEN_BADGE } from '../../utils/outcomeLabels';
import { SectionLabel } from '../../../../components/common/page/SectionLabel';
import { toolbarButtonSx } from '../../../../components/common/page/toolbarStyles';
import { NEW_TAB_LINK_PROPS, openInNewTab } from '../../../../utils/newTab';
import { ActivityBlock, type ActivityContext } from './ActivityBlock';
import { CardTail, FlowFigures } from './CardFigures';
import { TagChip } from './CardRow';
import { OtherGroupBadge } from './OtherGroupBadge';
import {
    EM_DASH,
    formatAge,
    formatProb,
    formatTimeLeft,
    formatUsd,
    priceMeta,
    priceValue,
} from './cardFormat';
import {
    PANEL_PAD_X,
    PANEL_ROW_HEIGHT,
    PanelFoot,
    PanelHead,
    panelCellSx,
    panelRowSx,
    panelTableHeadSx,
} from './panelParts';

const MAX_ROWS_VISIBLE = 10;
const PANEL_WIDTH_WIDE = 620;
const PANEL_WIDTH = 520;
const HEAD_IMAGE = 36;
const ROW_IMAGE = 18;
const BAR_WIDTH = 56;
const BAR_HEIGHT = 6;
const OUTCOME_MAXLEN_PANEL = 14;

const MODE_LABELS: Record<string, string> = {
    dependent: 'one winner',
    independent: 'independent markets',
};

const STATUS_LABELS: Record<string, string> = {
    closing_soon: 'closing soon',
    closed: 'closed',
    resolved: 'resolved',
};

function label(m: ScreenerMarket): string {
    return m.outcome_yes_label || m.question || m.market_id.slice(0, 8);
}

function gridColumns(withCapital: boolean, withLastTrade: boolean): string {
    const lead = `${ROW_IMAGE}px minmax(0, 1fr) ${BAR_WIDTH}px 36px`;
    const base = withLastTrade ? `${lead} 64px 60px` : `${lead} 60px`;
    return withCapital ? `${base} 116px` : base;
}

function bestByMarket(scores: BoardScore[]): Map<string, BoardScore> {
    const out = new Map<string, BoardScore>();
    for (const s of scores) {
        const prev = out.get(s.market_id);
        if (!prev || Math.abs(netCapital(s)) > Math.abs(netCapital(prev))) {
            out.set(s.market_id, s);
        }
    }
    return out;
}

function Thumb({
    src,
    text,
    size,
}: {
    src?: string | null;
    text: string;
    size: number;
}) {
    const theme = useTheme();
    return (
        <Box
            sx={{
                width: size,
                height: size,
                flexShrink: 0,
                borderRadius: size > 24 ? '6px' : '3px',
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: alpha(theme.palette.text.primary, 0.08),
                fontSize: size > 24 ? '14px' : '10px',
                fontWeight: 700,
                color: 'text.disabled',
            }}
        >
            {src ? (
                <Box
                    component="img"
                    src={src}
                    alt=""
                    loading="lazy"
                    sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                    }}
                />
            ) : (
                text.charAt(0).toUpperCase()
            )}
        </Box>
    );
}

export interface EventOutcomesPanelProps {
    event: ScreenerEvent;
    // Scores of the active group — their markets get the capital column.
    scores?: BoardScore[];
    accentColor?: string;
    now?: number;
    variant?: CardVariant;
    activityCtx?: ActivityContext | null;
    index?: EventConvictionIndex;
    activeGroup?: string;
    groupTitle?: string;
    activeTags?: readonly string[] | null;
    lastTrade?: boolean;
}

export function EventOutcomesPanel({
    event,
    scores = [],
    now,
    variant,
    activityCtx,
    index,
    activeGroup,
    groupTitle,
    activeTags,
    lastTrade = true,
}: EventOutcomesPanelProps) {
    const theme = useTheme();
    const nowMs = now ?? Date.now();

    const images = useEventOutcomeImages(event.event_id);
    const outcomesByMarket = useEventOutcomes(
        event.event_id,
        event.markets.some(needsTrueOutcomes)
    );

    const scoreByMarket = bestByMarket(scores);
    const withCapital = scoreByMarket.size > 0;
    const columns = gridColumns(withCapital, lastTrade);

    const rank =
        event.market_mode === 'dependent'
            ? (m: ScreenerMarket) => priceValue(m) ?? -1
            : (m: ScreenerMarket) => m.volume ?? -1;

    const ordered = [...event.markets].sort(
        (a, b) => Number(b.is_open) - Number(a.is_open) || rank(b) - rank(a)
    );

    const timeLeft = formatTimeLeft(event.end_date, nowMs);
    const closed = event.market_count - event.open_market_count;
    const mode = MODE_LABELS[event.market_mode];
    const status = STATUS_LABELS[event.resolution_status];
    const facts = [
        `${formatUsd(event.volume)} volume`,
        event.volume_24h ? `24h ${formatUsd(event.volume_24h)}` : null,
        status ?? (timeLeft.startsWith('in ') ? `ends ${timeLeft}` : timeLeft),
        `${event.market_count} ${event.market_count === 1 ? 'outcome' : 'outcomes'}`,
        closed > 0 ? `${closed} closed` : null,
        event.market_count > 1 && mode ? mode : null,
        event.category,
    ]
        .filter(Boolean)
        .join(' · ');

    const tags = orderTags(event.tags.filter(Boolean), activeTags);

    const isFlow = variant === 'capital' || variant === 'skill';
    const lead = isFlow ? leadScore(scores) : null;
    const leadLabels = sideLabels(lead, OUTCOME_MAXLEN_BADGE, {
        outcomes: lead ? outcomesByMarket.get(lead.market_id) : null,
    });

    const jumpMarket = activityCtx?.activity?.jump_market_id ?? null;
    const jumpLabel = jumpMarket
        ? label(
              event.markets.find(m => m.market_id === jumpMarket) ??
                  event.markets[0]
          )
        : null;

    const others = (index?.by_group ?? []).filter(
        g => g.group !== activeGroup && g.market_count > 0
    );

    // The panel is a hover preview of a list row or card: the event and its
    // markets open in a new tab, so the list stays where it is.
    const open = (href: string) => (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        openInNewTab(href);
    };

    const sectionSx = {
        px: PANEL_PAD_X,
        py: 1,
        borderBottom: 1,
        borderColor: 'divider',
    } as const;

    return (
        <Box sx={{ width: withCapital ? PANEL_WIDTH_WIDE : PANEL_WIDTH }}>
            <PanelHead
                leading={
                    <Thumb
                        src={event.image}
                        text={event.category ?? event.title ?? '?'}
                        size={HEAD_IMAGE}
                    />
                }
                title={
                    event.title ?? event.markets[0]?.question ?? event.event_id
                }
                meta={facts}
            >
                <Button
                    component="a"
                    href={eventHref(event.event_id, event.slug)}
                    {...NEW_TAB_LINK_PROPS}
                    size="small"
                    variant="outlined"
                    color="inherit"
                    endIcon={
                        <OpenInNewIcon sx={{ fontSize: '13px !important' }} />
                    }
                    onClick={open(eventHref(event.event_id, event.slug))}
                    sx={{
                        ...toolbarButtonSx,
                        borderColor: 'divider',
                        flexShrink: 0,
                        alignSelf: 'flex-start',
                        whiteSpace: 'nowrap',
                    }}
                >
                    Open event
                </Button>
            </PanelHead>

            {tags.length > 0 && (
                <Box
                    sx={{
                        ...sectionSx,
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 0.5,
                    }}
                >
                    {tags.map(t => (
                        <TagChip key={t.text} text={t.text} strong={t.strong} />
                    ))}
                </Box>
            )}

            {lead && (
                <Box sx={sectionSx}>
                    <SectionLabel sx={{ mb: 0.5 }}>
                        {groupTitle
                            ? `${groupTitle} · strongest position`
                            : 'Strongest position'}
                    </SectionLabel>
                    <CardTail columns={3}>
                        <FlowFigures
                            score={lead}
                            labels={leadLabels}
                            event={event}
                        />
                    </CardTail>
                </Box>
            )}

            {variant === 'activity' && activityCtx && (
                <Box sx={sectionSx}>
                    <SectionLabel sx={{ mb: 0.5 }}>Activity</SectionLabel>
                    <ActivityBlock ctx={activityCtx} marketLabel={jumpLabel} />
                </Box>
            )}

            <Box sx={{ px: 0.75, py: 1 }}>
                <Box
                    sx={{
                        ...panelRowSx,
                        display: 'grid',
                        gridTemplateColumns: columns,
                        columnGap: 1,
                    }}
                >
                    <Box />
                    <Typography component="span" sx={panelTableHeadSx}>
                        Outcome
                    </Typography>
                    <Typography
                        component="span"
                        sx={{
                            ...panelTableHeadSx,
                            gridColumn: 'span 2',
                            textAlign: 'right',
                        }}
                    >
                        Probability
                    </Typography>
                    {lastTrade && (
                        <Typography
                            component="span"
                            sx={{ ...panelTableHeadSx, textAlign: 'right' }}
                        >
                            Last trade
                        </Typography>
                    )}
                    <Typography
                        component="span"
                        sx={{ ...panelTableHeadSx, textAlign: 'right' }}
                    >
                        Volume
                    </Typography>
                    {withCapital && (
                        <Typography
                            component="span"
                            sx={{ ...panelTableHeadSx, textAlign: 'right' }}
                        >
                            Net capital
                        </Typography>
                    )}
                </Box>

                <Box
                    sx={{
                        maxHeight: PANEL_ROW_HEIGHT * MAX_ROWS_VISIBLE,
                        overflowY: 'auto',
                        scrollbarWidth: 'thin',
                    }}
                >
                    {ordered.map(m => {
                        const price = priceMeta(m, nowMs);
                        const score = scoreByMarket.get(m.market_id);
                        const side = score ? netSide(score) : null;
                        const rowLabels = sideLabels(m, OUTCOME_MAXLEN_PANEL, {
                            outcomes: outcomesByMarket.get(m.market_id),
                        });
                        const rowSide =
                            side === 'NO' ? rowLabels.no : rowLabels.yes;
                        const href = eventMarketHref(
                            event.event_id,
                            m.market_id,
                            event.slug
                        );
                        const lastTradeText = m.live_prob_at
                            ? formatAge(m.live_prob_at, nowMs)
                            : price.value === null
                              ? EM_DASH
                              : '> 24h ago';
                        return (
                            <Box
                                key={m.market_id}
                                component="a"
                                href={href}
                                {...NEW_TAB_LINK_PROPS}
                                title={m.question ?? undefined}
                                onClick={open(href)}
                                sx={{
                                    ...panelRowSx,
                                    display: 'grid',
                                    gridTemplateColumns: columns,
                                    columnGap: 1,
                                    opacity: m.is_open ? 1 : 0.45,
                                    color: 'inherit',
                                    textDecoration: 'none',
                                    cursor: 'pointer',
                                    '&:hover': { bgcolor: 'action.hover' },
                                }}
                            >
                                <Thumb
                                    src={images.get(m.market_id)}
                                    text={label(m)}
                                    size={ROW_IMAGE}
                                />

                                <Typography
                                    component="span"
                                    sx={{
                                        ...panelCellSx,
                                        minWidth: 0,
                                        color: 'text.primary',
                                    }}
                                >
                                    {label(m)}
                                </Typography>

                                <Box
                                    sx={{
                                        width: BAR_WIDTH,
                                        height: BAR_HEIGHT,
                                        borderRadius: '3px',
                                        overflow: 'hidden',
                                        bgcolor: alpha(
                                            theme.palette.text.primary,
                                            0.12
                                        ),
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: `${Math.max(0, Math.min(1, price.value ?? 0)) * 100}%`,
                                            height: '100%',
                                            bgcolor: 'primary.main',
                                        }}
                                    />
                                </Box>

                                <Typography
                                    component="span"
                                    title={price.title}
                                    sx={{
                                        ...panelCellSx,
                                        textAlign: 'right',
                                        fontWeight: 600,
                                        color: 'text.primary',
                                    }}
                                >
                                    {price.value === null
                                        ? EM_DASH
                                        : formatProb(price.value)}
                                </Typography>

                                {lastTrade && (
                                    <Typography
                                        component="span"
                                        title={price.title}
                                        sx={{
                                            ...panelCellSx,
                                            textAlign: 'right',
                                            color: price.fresh
                                                ? 'text.primary'
                                                : 'text.disabled',
                                        }}
                                    >
                                        {lastTradeText}
                                    </Typography>
                                )}

                                <Typography
                                    component="span"
                                    sx={{
                                        ...panelCellSx,
                                        textAlign: 'right',
                                        color: 'text.secondary',
                                    }}
                                >
                                    {formatUsd(m.volume ?? 0)}
                                </Typography>

                                {withCapital && (
                                    <Typography
                                        component="span"
                                        title={
                                            score ? rowSide.title : undefined
                                        }
                                        sx={{
                                            ...panelCellSx,
                                            textAlign: 'right',
                                            fontWeight: score ? 700 : 400,
                                            color: score
                                                ? sideColor(
                                                      rowLabels,
                                                      side,
                                                      theme
                                                  )
                                                : 'text.disabled',
                                        }}
                                    >
                                        {!score
                                            ? EM_DASH
                                            : side === null
                                              ? formatUsd(0)
                                              : `${formatUsd(Math.abs(netCapital(score)))} ${rowSide.text}`}
                                    </Typography>
                                )}
                            </Box>
                        );
                    })}
                </Box>
            </Box>

            <PanelFoot>
                {others.length > 0 && (
                    <>
                        <span>Also positioned</span>
                        <OtherGroupBadge
                            index={index}
                            activeGroup={activeGroup ?? ''}
                        />
                    </>
                )}
                <Box sx={{ flex: 1 }} />
                <span>Click an outcome to open it in the event view</span>
            </PanelFoot>
        </Box>
    );
}
